import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Disable/enable body scroll when menu opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Home Buyer", path: "/home-buyer" },
    { name: "Services", path: "/services" },
    { name: "FAQs", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "navbar-white" : "bg-white/95 shadow-sm"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center logo-link"
              data-testid="navbar-logo"
              onClick={() => setIsOpen(false)}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/j8sybs2h_rera%20hub.jpeg" 
                alt="RERA Hub Logo" 
                className="navbar-logo h-12 md:h-14 w-auto transition-transform duration-300 hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors duration-300 ${
                    isActive(link.path)
                      ? "text-[#C9A14A]"
                      : "text-[#1A1A1A] hover:text-[#C9A14A]"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(true)}
              className="md:hidden p-2 rounded-md bg-[#C9A14A] text-white hover:bg-[#B8903F] transition-colors duration-300"
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`md:hidden fixed inset-0 z-[9999] transition-all duration-300 ease-in-out ${
          isOpen 
            ? 'opacity-100 visible' 
            : 'opacity-0 invisible pointer-events-none'
        }`}
        data-testid="mobile-menu-overlay"
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute top-0 right-0 w-full h-full bg-white shadow-2xl transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu"
        >
          {/* Menu Header */}
          <div className="flex justify-between items-center px-6 py-5 border-b border-slate-100">
            <Link
              to="/"
              onClick={() => handleLinkClick('/')}
              className="flex items-center"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/j8sybs2h_rera%20hub.jpeg" 
                alt="RERA Hub Logo" 
                className="h-10 w-auto"
              />
            </Link>
            
            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-full bg-[#C9A14A] text-white hover:bg-[#B8903F] transition-colors duration-300 shadow-md"
              data-testid="mobile-menu-close"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* Menu Items */}
          <div className="px-6 py-8">
            {navLinks.map((link, index) => (
              <div key={link.path}>
                <button
                  onClick={() => handleLinkClick(link.path)}
                  className={`w-full text-left py-5 text-xl font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? "text-[#C9A14A]"
                      : "text-[#1A1A1A] hover:text-[#C9A14A] hover:pl-2"
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
                {index < navLinks.length - 1 && (
                  <div className="border-b border-slate-100" />
                )}
              </div>
            ))}
          </div>

          {/* Contact Info at Bottom */}
          <div className="absolute bottom-0 left-0 right-0 px-6 py-6 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-[#555555] mb-2">Need assistance?</p>
            <a 
              href="tel:+919404515431" 
              className="text-[#C9A14A] font-semibold text-lg hover:text-[#B8903F] transition-colors"
            >
              +91-9404515431
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
